// AIT-529 — fires the affiliate grant (POST /invite/complete) in the
// BACKGROUND, once, on mount.
//
// The grant is our bookkeeping, not the client's task: they came to connect a
// channel. Blocking the UI on it cost ~1.5s of spinner (auth + workspaces +
// grant, serialized) before they could see anything. Now the channel picker
// paints immediately and the grant lands underneath it.
//
// Failure is silent by design. The consent row stays retryable for 24h and
// the backend already logs the failure -- telling the client "we could not
// add your partner" names an internal relationship they never asked about
// and cannot act on.
import { useEffect, useRef } from 'react';
import { useFetch } from '@/hooks/use-fetch';
import { peekInviteContext, clearInviteContext } from '@/lib/invite-storage';

// Codex review (P1): a single attempt could strand the grant. The client
// leaves this page for the OAuth round-trip and ends on the done screen, so
// "the context survives for a later visit" is not a retry — there may be no
// later visit. Retry transient failures here, and mount the hook again on the
// connecting page so the flow gets a second, later attempt.
const RETRY_DELAYS_MS = [2_000, 6_000];

export function useCompleteInvite(): void {
  const fetchWithAuth = useFetch();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const context = peekInviteContext();
    if (!context) return;

    let cancelled = false;

    /** True when the consent is settled and nothing should retry it. */
    const attempt = async (): Promise<boolean> => {
      let res: Response | null = null;
      try {
        res = await fetchWithAuth(
          '/invite/complete',
          { method: 'POST', body: JSON.stringify({ consentId: context.consentId }) },
          { suppressGlobalError: true },
        );
      } catch {
        // Network blip -- the request never reached the backend, so the
        // consent row is untouched and still retryable. Keep the context.
        return false;
      }
      // Terminal: success, or a definitively-dead consent (404 not found /
      // 410 expired). Everything else (401/403/409/5xx) is retryable, so the
      // context survives for the next attempt.
      if (res.ok || res.status === 404 || res.status === 410) {
        clearInviteContext();
        return true;
      }
      return false;
    };

    void (async () => {
      if (await attempt()) return;
      for (const delay of RETRY_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (cancelled) return;
        if (await attempt()) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth]);
}
